import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import { ActionSection, ModalHeader, SideCancelBtn, SubmitBtn } from "../Styles/InputStyles";
import { useSelector } from "react-redux";

const FilterModal = ({
  onApplyFilter,
  handleClose,
  currentFilter = "",
  currentFilterVal = "",
  staffData,
  selectedLocationId,
  setSelectedLocationId,
}) => {
  const locations = useSelector((state) => state.LocationsReducer.locations);
  const filteredLocations = locations.filter(location => location.id !== 0);
  const [activeType, setActiveType] = useState("tenantProfile");
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  // const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [height, setHeight] = useState(window.innerHeight);

  const filterGroups = [
    {
      key: "tenantProfile",
      title: "Tenant Profile",
      subGroups: [
        {
          title: "KYC Status",
          options: [
            { label: "Verified", value: "V" },
            { label: "Unverified", value: "UV" },
          ],
        },
        {
          title: "Gender",
          options: [
            { label: "Male", value: "GM" },
            { label: "Female", value: "GF" },
            { label: "Others", value: "GO" },
          ],
        },
        {
          title: "App Status",
          options: [
            { label: "Installed", value: "I" },
            { label: "Not Installed", value: "NI" },
          ],
        },
      ],
    },
    {
      key: "legalCompliance",
      title: "Legal",
      subGroups: [
        {
          title: "Rent Agreement",
          options: [
            { label: "Signed", value: "RA" },
            { label: "Pending", value: "RAP" },
          ],
        },
        {
          title: "Police Verification",
          options: [
            { label: "Completed", value: "PV" },
            { label: "Pending", value: "PVP" },
          ],
        },
        {
          title: "Rental Bond",
          options: [
            { label: "Availed", value: "RB" },
            { label: "Not Availed", value: "RBN" },
            { label: "Not Allowed", value: "RBNA" },
          ],
        },
      ],
    },
    {
      key: "financial",
      title: "Financial",
      subGroups: [
        {
          title: "Online Payment",
          options: [
            { label: "Enabled", value: "OE" },
            { label: "Not Enabled", value: "ONE" },
          ],
        },
        {
          title: "Security Deposit",
          options: [
            { label: "Zero", value: "ZS" },
            { label: "1 Month", value: "1RS" },
            { label: "1.5 Month", value: "1.5RS" },
            { label: "2 Month", value: "2RS" },
          ],
        },
      ],
    },
    {
      key: "stay",
      title: "Stay",
      subGroups: [
        {
          title: "Stay Type",
          options: [{ label: "Short Stay", value: "SS" }],
        },
      ],
    },
    {
      key: "location",
      title: "Location",
      subGroups: [
        {
          title: "Select Location",
          options:
            filteredLocations && filteredLocations.length > 0
              ? filteredLocations.map((loc) => ({
                label: loc.name,
                value: `LOC_${loc.id}`,
                locationId: loc.id,
              }))
              : [
                {
                  label: "No locations available",
                  value: "NO_LOC",
                  disabled: true,
                },
              ],
        },
      ],
    },
    {
      key: "sortBy",
      title: "Sort By",
      subGroups: [
        {
          title: "Sort Options",
          options: [
            { label: "Room Number", value: "RS" },
            { label: "Created On", value: "CS" },
          ],
        },
      ],
    },
  ];

  const allFilterGroups =
    staffData && staffData.length > 0
      ? [
        ...filterGroups,
        {
          key: "bookedBy",
          title: "Booked By",
          subGroups: [
            {
              title: "Staff Members",
              options: staffData.map((staff) => ({
                label: staff.name,
                value: `${staff.id}`,
                staffId: staff.id,
                isStaff: true,
              })),
              isMultiSelect: true,
            },
          ],
        },
      ]
      : filterGroups;

  const isFilterSelected = () => {
    if (activeType === "bookedBy" && selectedStaffIds.length > 0) {
      return true;
    }
    if (activeType === "location" && selectedLocationId) {
      return true;
    }
    if (selectedFilter && selectedFilter.value) {
      return true;
    }
    return false;
  };

  const isAnyFilterSelected = isFilterSelected();

  useEffect(() => {
    setSelectedFilter(null);
    setSelectedStaffIds([]);
    // setSelectedLocationId(null);

    if (!currentFilter) return;

    if (currentFilter === "BY" && currentFilterVal) {
      setActiveType("bookedBy");
      const staffIds = currentFilterVal
        .split(",")
        .map((idStr) => parseInt(idStr.trim()))
        .filter((id) => !isNaN(id));

      setSelectedStaffIds(staffIds);
      if (staffIds.length > 0 && staffData) {
        const selectedStaffNames = staffIds
          .map((id) => {
            const staff = staffData.find((s) => s.id === id);
            return staff?.name || `Staff ${id}`;
          })
          .filter(Boolean);

        setSelectedFilter({
          type: "Booked By",
          subType: "Staff Members",
          value: currentFilter,
          filterVal: currentFilterVal,
          label:
            selectedStaffNames.length > 2
              ? `${selectedStaffNames.slice(0, 2).join(", ")} +${selectedStaffNames.length - 2} more`
              : selectedStaffNames.join(", "),
          isBookedBy: true,
        });
      }
    } else if (currentFilter === "L" && currentFilterVal) {
      setActiveType("location");
      const locationId = parseInt(currentFilterVal);
      const location = filteredLocations?.find((loc) => loc.id === locationId);

      setSelectedLocationId(locationId);
      if (location) {
        setSelectedFilter({
          type: "Location",
          subType: "Location",
          value: currentFilter,
          filterVal: currentFilterVal,
          label: location.name,
          isLocation: true,
        });
      }
    } else {
      for (const group of allFilterGroups) {
        for (const subGroup of group.subGroups) {
          const match = subGroup.options.find((o) => o.value === currentFilter);
          if (match) {
            setActiveType(group.key);
            setSelectedFilter({
              type: group.title,
              subType: subGroup.title,
              value: match.value,
              label: match.label,
              isBookedBy: false,
              isLocation: false,
            });
            return;
          }
        }
      }
    }
  }, [currentFilter, currentFilterVal, staffData,]);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleStaffSelect = (staffId) => {
    setSelectedStaffIds((prev) => {
      if (prev.includes(staffId)) {
        return prev.filter((id) => id !== staffId);
      } else {
        return [...prev, staffId];
      }
    });
  };

  const handleLocationSelect = (locationId, locationName) => {
    setSelectedLocationId(locationId);
    setSelectedFilter({
      type: "Location",
      subType: "Location",
      value: "L",
      filterVal: locationId.toString(),
      label: locationName,
      isLocation: true,
      isBookedBy: false,
    });
  };

  const handleApply = () => {
    if (activeType === "bookedBy") {
      if (selectedStaffIds.length === 0) {
        toast.error("Please select at least one staff member.");
        return;
      }

      const selectedStaffNames = selectedStaffIds
        .map((id) => {
          const staff = staffData?.find((s) => s.id === id);
          return staff?.name || `Staff ${id}`;
        })
        .filter(Boolean);

      const label =
        selectedStaffNames.length > 2
          ? `${selectedStaffNames.slice(0, 2).join(", ")} +${selectedStaffNames.length - 2} more`
          : selectedStaffNames.join(", ");

      onApplyFilter(
        "BY",
        label,
        "Booked By",
        "Staff Members",
        true,
        selectedStaffIds,
      );
    } else if (activeType === "location") {
      if (!selectedLocationId) {
        toast.error("Please select a location.");
        return;
      }

      const location = filteredLocations?.find((loc) => loc.id === selectedLocationId);

      onApplyFilter(
        "L",
        location?.name || `Location ${selectedLocationId}`,
        "Location",
        "Location",
        false,
        selectedLocationId,
      );
    } else {
      if (!selectedFilter) {
        toast.error("Please select at least one filter before applying.");
        return;
      }
      onApplyFilter(
        selectedFilter.value,
        selectedFilter.label,
        selectedFilter.type,
        selectedFilter.subType,
        false,
      );
    }
  };

  const handleClear = () => {
    setSelectedFilter(null);
    setSelectedStaffIds([]);
    setSelectedLocationId(null);
  };

  const activeGroup =
    allFilterGroups.find((g) => g.key === activeType) || allFilterGroups[0];

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ModalHeader>
        <h1 className="text-lg font-medium text-white">Filters</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>

      <Body>
        <LeftPane>
          {allFilterGroups.map((group) => {
            const active = activeType === group.key;
            return (
              <TypeItem
                key={group.key}
                active={active}
                onClick={() => {
                  setActiveType(group.key);
                  if (
                    group.key !== activeType &&
                    group.key !== "bookedBy" &&
                    group.key !== "location"
                  ) {
                    setSelectedFilter(null);
                    setSelectedLocationId(null);
                  }
                }}
              >
                {group.title}
              </TypeItem>
            );
          })}
        </LeftPane>

        <RightPane>
          <FilterTitle>{activeGroup.title}</FilterTitle>

          <ScrollableContent className="max-h-[calc(100vh-180px)]">
            {activeGroup.subGroups.map((subGroup, index) => {
              const isMultiSelect = subGroup.isMultiSelect;

              return (
                <div key={`${subGroup.title}-${index}`} className="mb-6">
                  <SubGroupTitle>{subGroup.title}</SubGroupTitle>
                  <div className="space-y-2">
                    {subGroup.options.map((option) => {
                      if (option.disabled) {
                        return (
                          <OptionRow key={option.value} className="opacity-50">
                            <span className="leading-tight text-sm text-gray-400">
                              {option.label}
                            </span>
                          </OptionRow>
                        );
                      }

                      if (isMultiSelect) {
                        const isSelected = selectedStaffIds.includes(
                          option.staffId,
                        );

                        return (
                          <OptionRow
                            key={option.value}
                            onClick={() => handleStaffSelect(option.staffId)}
                            className="cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              style={{ margin: 0, width: 16, height: 16 }}
                            />
                            <span className="leading-tight text-sm text-gray-600">
                              {option.label}
                            </span>
                          </OptionRow>
                        );
                      } else {
                        if (activeType === "location") {
                          const isSelected =
                            selectedLocationId === option.locationId;

                          return (
                            <OptionRow
                              key={option.value}
                              onClick={() =>
                                handleLocationSelect(
                                  option.locationId,
                                  option.label,
                                )
                              }
                              className="cursor-pointer"
                            >
                              <input
                                type="radio"
                                checked={isSelected}
                                readOnly
                                style={{ margin: 0, width: 16 }}
                              />
                              <span className="leading-tight text-sm text-gray-600">
                                {option.label}
                              </span>
                            </OptionRow>
                          );
                        } else {
                          const checked =
                            selectedFilter?.value === option.value;

                          return (
                            <OptionRow
                              key={option.value}
                              onClick={() =>
                                setSelectedFilter({
                                  type: activeGroup.title,
                                  subType: subGroup.title,
                                  value: option.value,
                                  label: option.label,
                                  isBookedBy: false,
                                  isLocation: false,
                                })
                              }
                              className="cursor-pointer"
                            >
                              <input
                                type="radio"
                                checked={checked}
                                readOnly
                                style={{ margin: 0, width: 16 }}
                              />
                              <span className="leading-tight text-sm text-gray-600">
                                {option.label}
                              </span>
                            </OptionRow>
                          );
                        }
                      }
                    })}
                  </div>

                  {isMultiSelect && selectedStaffIds.length > 0 && (
                    <div className="mt-3 text-xs text-primary-color font-medium">
                      {selectedStaffIds.length} staff member
                      {selectedStaffIds.length !== 1 ? "s" : ""} selected
                    </div>
                  )}


                </div>
              );
            })}
          </ScrollableContent>
        </RightPane>
      </Body>

      <ActionSection>
        {isAnyFilterSelected ? (
          <SideCancelBtn
            type="button"
            onClick={handleClear}
            className="!text-red-500 hover:!bg-red-50"
          >
            Clear
          </SideCancelBtn>
        ) : (
          <SideCancelBtn type="button" onClick={handleClose}>
            Cancel
          </SideCancelBtn>
        )}
        <SubmitBtn type="submit" onClick={handleApply}>
          Apply Filter
        </SubmitBtn>
      </ActionSection>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const Body = tw.div`flex flex-1 overflow-hidden`;
const LeftPane = tw.div`w-1/3 bg-gray-50 border-r overflow-y-auto`;
const TypeItem = tw.div`
  px-4 py-4 cursor-pointer text-md
  ${({ active }) =>
    active
      ? "bg-blue-50 text-primary-color font-medium border-l-4 border-primary-color"
      : "text-gray-700 hover:bg-gray-100"}
`;
const RightPane = tw.div`flex-1 flex flex-col py-4 overflow-hidden`;
const FilterTitle = tw.h3`text-md font-medium text-primary-color mb-4 px-4`;
const ScrollableContent = tw.div`flex-1 overflow-y-auto px-4`;
const SubGroupTitle = tw.h4`text-sm font-medium text-gray-700 mb-3`;
const OptionRow = tw.div`
  flex items-center gap-3 py-2 px-1
  text-sm text-gray-600 rounded-md hover:bg-gray-50
`;

export default FilterModal;
